import React from 'react';
import { FaCheckSquare, FaRegSquare } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Data from './Data';
import PillObservacao from './PillObservacao';
import { FinCheck, FinField, FinInput, FinNumber, FinSwitch, FinTextarea } from './FinField';
import {
    LOCAIS, HORARIOS, EXEMPLO_LOCAL, LOCAL_ACEITO, pedeIda, pedeRetorno,
    comLocalDigitado, comDirecaoLigada, comSemTrecho,
} from '../util/malhaAereo';
import '../css/financeiro-parcela-pagar.css';

// O FORMULÁRIO DE UM TRECHO, inteiro e num lugar só.
//
// É a mesma peça na solicitação (onde o RSVP pede) e no modal da malha (onde o Aéreo sobrescreve).
// Não é um layout simples: as pontas se dividem em dois níveis, os badges de herança e de perna têm
// regra própria, e as quebras dependem de container queries medidas uma a uma. Duas cópias disso
// divergiriam na primeira correção — por isso o arquivo é um só, e as telas só ligam os fios.
//
// O componente não guarda estado e não sabe de onde vem o trecho nem para onde vai: recebe o
// objeto, as referências de herança e os avisos de mudança. O que ele SABE são as regras da tela —
// de onde cada campo herda, quando o badge acende, qual é o teto de passageiros — e é aqui que elas
// ficam, para não serem reescritas em cada chamador.
//
// O que difere entre as duas telas entra por prop, e o que não é passado simplesmente não aparece:
// o select da origem da solicitação, o "Sem trecho", o teto de passageiros e a observação.

// ---------------------------------------------------------------------------------------------
// Medidas. Todas saídas de medição na tela real — trocar qualquer uma muda onde a linha quebra.
// ---------------------------------------------------------------------------------------------

// Rótulo de campo numa linha só, com reticências quando não cabe: quebrado, ele deixava o campo
// mais alto que os vizinhos da mesma fileira.
const ROTULO_UMA_LINHA = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };
// O badge do herdado não tem o teto de 60% da linha que o `.fin-pp__herdado` aplica — com ele, o
// badge era cortado antes de o campo chegar ao piso. O teto é o espaço que SOBRA na linha: ele
// cresce até onde cabe e, faltando espaço, corta com reticências (a classe já faz o corte) antes
// do rótulo — o fator de encolhimento alto faz ele ceder primeiro. O texto inteiro segue no title.
const BADGE_HERDADO = { flexShrink: 100000, minWidth: 24, maxWidth: '100%' };

// A divisão é em DOIS níveis, e é o que impede a largura de 1/3: a linha parte ao meio entre origem
// e destino, e cada metade se divide entre as pernas abertas dela. Assim um campo é sempre 1/2
// (ponta com uma perna) ou 1/4 (ponta com as duas) — nunca um terço, que dependeria de quantas
// pernas a OUTRA ponta abriu e faria a largura pular sem motivo visível.
// A ponta é SEMPRE 50% ou 100% da linha, nunca um tamanho tirado do conteúdo: a base é meia linha
// (menos metade do vão, senão as duas metades mais o vão passariam de 100% e quebrariam sempre).
// Não cabendo meia linha, o piso abaixo empurra a ponta para fora da linha e ela vira 100%.
const ESTILO_PONTA = { flex: '1 1 calc(50% - 8px)' };
// Piso do campo de local. O placeholder curto ("Cidade, Aeroporto, IATA ou todos") mede 207px na
// fonte da tela e o input soma 30px de padding e borda; 200 deixa o fim dele reticente no aperto,
// mas mantém legível o que foi digitado — era em 132 que o texto cortava no meio da palavra.
const LARGURA_MIN_LOCAL = 200;
const ESTILO_CAMPO_LOCAL = { flex: '1 1 0', minWidth: `min(100%, ${LARGURA_MIN_LOCAL}px)` };
// Piso da ponta: o que as pernas abertas dela pedem, mas nunca mais que a linha inteira. É essa
// conta que garante a ordem pedida — enquanto couber, ida e volta ficam lado a lado; faltando
// espaço, quem quebra primeiro é a linha de fora (DESTINOS inteiro abaixo de ORIGENS), e só quando
// nem uma ponta sozinha cabe é que ida e volta se empilham, já dentro da ponta.
const pisoDaPonta = (pernas) => `min(100%, ${pernas * LARGURA_MIN_LOCAL + (pernas - 1) * 16}px)`;
// Placeholder do campo vazio com o badge do herdado desligado: o campo não vale o herdado.
const LOCAL_SEM_HERDADO = LOCAL_ACEITO;

// Na linha da direção, o rótulo da data e o switch de cada horário têm a mesma altura, para os
// campos embaixo alinharem; as horas dividem o bloco entre "de" e "até".
const CABECALHO_BLOCO = { height: 28, display: 'flex', alignItems: 'center' };
// Switch de horário desligado: só a altura dele (20px). Os 7px embaixo põem o centro dele no centro
// do input de 42px quando a linha se alinha pela base.
const SWITCH_DESLIGADO = { height: 20, display: 'flex', alignItems: 'center', marginBottom: 7 };
// 228 é a largura em que o bloco fica EXATO: dois inputs de hora no tamanho do conteúdo (87 cada —
// "17:32", a margem que o Chrome põe antes do relógio, o relógio, padding e borda), mais "de", "até"
// e os três vãos. Base e piso são o mesmo número de propósito: é a BASE que decide as quebras.
const LARGURA_HORARIO_LIGADO = 228;
const LARGURA_HORARIO_DESLIGADO = 170;
const larguraDosHorarios = (ligados) => ligados
    .reduce((soma, ligado) => soma + (ligado ? LARGURA_HORARIO_LIGADO : LARGURA_HORARIO_DESLIGADO), 0)
    + 16 * (ligados.length - 1);
const HORA_NO_BLOCO = { paddingLeft: 10, paddingRight: 8, minWidth: 0, flex: 1 };
// Piso e teto da data saem do CABEÇALHO, que pede mais largura que o input: com o badge do herdado
// inteiro e o vão, o que sobra é do rótulo. 116 é onde ele chega em "Data…"; 170 é o tamanho cheio.
const LARGURA_DATA_PISO = 116;

// ---------------------------------------------------------------------------------------------
// Peças internas
// ---------------------------------------------------------------------------------------------

/**
 * Rótulo do campo com o valor herdado em badge ao lado: é o que vale se o trecho não trocar.
 * Embaixo do campo a dica empurrava a fileira, que alinha os campos pela base, e o layout pulava ao
 * digitar. `herdado` é { valor, dica, ativo, alternar }; com `alternar`, o badge é clicável.
 *
 * A linha do rótulo nunca quebra: sem espaço, quem encolhe é o RÓTULO, com reticências — o badge
 * fica inteiro, e o badge de ação não encolhe.
 */
function rotuloHerdado(rotulo, herdado, acao = null, oculto = false, editavel = true) {
    return (
        <div className="fin-pp__field-head" style={{ justifyContent: 'flex-start', gap: 8, minWidth: 0 }}>
            <label className="fin-pp__label" style={ROTULO_UMA_LINHA}>{rotulo}</label>
            {/* Sem perna ativa o badge do herdado fica ESCONDIDO, não removido: some da vista sem
                deixar a linha do cabeçalho mudar de altura quando as pernas vão e voltam.
                display:contents porque o invólucro NÃO pode virar caixa: como caixa, ele passava a
                ser o bloco de referência do `max-width: 60%` do badge e o valor saía cortado
                ("Sã...") mesmo com a linha inteira livre. Sem caixa, o badge volta a medir contra o
                cabeçalho, e a visibilidade (que é herdada) continua chegando nele. */}
            <span style={{ display: 'contents', ...(oculto ? { visibility: 'hidden' } : null) }}>
                {herdado?.valor && (herdado.alternar ? (
                    <button type="button" className={'fin-pp__minitag fin-pp__herdado' + (herdado.ativo ? ' is-info' : '')}
                        style={{ ...BADGE_HERDADO, fontFamily: 'inherit', cursor: editavel ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        aria-pressed={!!herdado.ativo} disabled={!editavel} title={herdado.dica}
                        onClick={herdado.alternar}>
                        {/* Caixa de marcar à direita do local: marcada, o campo vale ou detalha o lugar de
                            cima; desmarcada, o texto troca o lugar. Diz o estado e que dá para clicar, e
                            não se confunde com o × das pernas, que fecha o campo e fica à esquerda. O
                            "em" é fixo, o mesmo para todo local herdado. */}
                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>em {herdado.valor}</span>
                        {herdado.ativo ? <FaCheckSquare size={10} style={{ flexShrink: 0 }} /> : <FaRegSquare size={10} style={{ flexShrink: 0 }} />}
                    </button>
                ) : (
                    <span className="fin-pp__minitag is-info fin-pp__herdado" title={herdado.dica} style={BADGE_HERDADO}>
                        {herdado.valor}
                    </span>
                ))}
            </span>
            {acao && <span style={{ marginLeft: 'auto', flexShrink: 0 }}>{acao}</span>}
        </div>
    );
}

/** Badge do herdado de um local: o texto e a dica mudam conforme o campo está vazio ou preenchido. */
function badgeDoHerdado(t, local, de, ref, aoAlternar) {
    if (!ref.valor) return null;
    const digitado = String(t[local.campo] ?? '').trim();
    const ligado = !!t[local.herdado];
    let dica;
    if (!digitado) dica = ligado ? `Vazio vale ${de}: ${ref.valor}` : `Ligue para valer ${de}: ${ref.valor}`;
    else dica = ligado ? `Detalha ${de}: ${ref.base} (${digitado})` : `Troca ${de}. Ligue para detalhar: ${ref.base} (${digitado})`;
    return { valor: ref.valor, ativo: ligado, dica, alternar: () => aoAlternar(local, !ligado) };
}

/** Uma PONTA do trecho — origem ou destino — com os campos das pernas que têm lugar próprio. */
function Ponta({ trecho: t, chave, referencias, editavel, onDigitar, onAlternarHerdado, onAlternarCampo }) {
    const local = LOCAIS[chave];
    const pai = String(referencias.origemPai ?? '').trim();
    const destinoPedido = String(referencias.destino ?? '').trim();
    // De onde cada campo herda, sempre do PEDIDO e espelhado na volta: a origem da volta vale o
    // destino do pedido, e o destino da volta vale a origem da solicitação. Nunca o que foi
    // digitado na ida — trecho não herda de si mesmo.
    const daOrigem = { base: pai, valor: pai, detalhe: '' };
    const doDestino = { base: destinoPedido, valor: destinoPedido, detalhe: '' };
    const heranca = chave === 'origem'
        ? { ida: { de: 'a origem', ref: daOrigem }, volta: { de: 'o destino da solicitação', ref: doDestino } }
        : { ida: { de: 'o destino da solicitação', ref: doDestino }, volta: { de: 'a origem', ref: daOrigem } };
    const pedida = { ida: pedeIda(t), volta: pedeRetorno(t) };
    const abertas = ['ida', 'volta'].filter(perna => pedida[perna] && t[local[perna].aberto]);
    // UMA label para a ponta, nunca duas: com as duas pernas abertas ela vira plural e fica só no
    // primeiro campo — cada campo já é identificado pelo badge da perna dele.
    const rotuloDaPonta = abertas.length > 1 ? local.plural
        : (abertas[0] === 'volta' ? `${local.nome} da volta` : local.nome);

    // Badge da perna, à direita do campo. Aceso (azul, com ×) é o campo que existe e pode fechar;
    // apagado é a perna sem campo próprio, e clicar abre um. O × some quando fechar não é possível
    // — a ponta nunca fica sem nenhum campo, senão não há onde escrever.
    const badgePerna = (campo, perna, aceso, podeFechar) => {
        const dica = aceso
            ? (podeFechar
                ? (perna === 'volta'
                    ? 'Fecha o campo da volta: ela volta a espelhar a ida.'
                    : 'Fecha o campo da ida: ela passa a valer o herdado.')
                : 'A ponta precisa de pelo menos um campo.')
            : (perna === 'volta'
                ? 'Abre um lugar só para a volta. Sem ele, a volta espelha a ida.'
                : 'Abre um lugar só para a ida.');
        const travado = !editavel || (aceso && !podeFechar);
        return (
            <button key={perna + (aceso ? '-aceso' : '')} type="button" className={'fin-pp__minitag' + (aceso ? ' is-info' : '')}
                style={{ fontFamily: 'inherit', whiteSpace: 'nowrap', cursor: travado ? 'default' : 'pointer' }}
                aria-pressed={aceso} disabled={travado} title={dica}
                onClick={() => onAlternarCampo(campo, !aceso)}>
                {aceso && podeFechar ? `× ${perna}` : perna}
            </button>
        );
    };
    // Ida sempre à esquerda da volta, aberta ou fechada: a ordem não dança conforme o estado.
    const badgesFechadas = (perna) => ['ida', 'volta']
        .filter(outra => outra !== perna && pedida[outra] && !abertas.includes(outra))
        .map(outra => <span key={outra}>{badgePerna(local[outra], outra, false, true)}</span>);
    const badgesDoCampo = (perna) => (perna === 'ida'
        ? [badgePerna(local.ida, 'ida', true, true), ...badgesFechadas('ida')]
        : [...badgesFechadas('volta'), badgePerna(local.volta, 'volta', true, true)]);
    // Com as duas abertas o espaço é metade: o placeholder perde o exemplo, que não caberia.
    const exemplo = abertas.length > 1 ? LOCAL_ACEITO : EXEMPLO_LOCAL;
    const badge = (perna) => badgeDoHerdado(t, local[perna], heranca[perna].de, heranca[perna].ref, onAlternarHerdado);

    // A ponta inteira num invólucro: é ele que ocupa metade da linha, e os campos dela dividem
    // essa metade entre si.
    const ponta = (filhos) => (
        <div className="d-flex flex-wrap align-items-end"
            style={{ gap: '14px 16px', ...ESTILO_PONTA, minWidth: pisoDaPonta(Math.max(1, abertas.length)) }}>
            {filhos}
        </div>
    );

    // Nenhuma perna aberta: a ponta inteira herda. Fica um campo só, desabilitado e com o badge do
    // herdado escondido — o texto continua no estado (o Salvar é que grava vazio) e as pernas
    // apagadas esperam o clique.
    if (!abertas.length) {
        const perna = pedida.ida ? 'ida' : 'volta';
        return ponta(
            <FinField key={chave} style={ESTILO_CAMPO_LOCAL}>
                {rotuloHerdado(local.nome, badge(perna),
                    <span className="d-inline-flex align-items-center gap-1">
                        {['ida', 'volta'].filter(x => pedida[x]).map(x => (
                            <span key={x}>{badgePerna(local[x], x, false, true)}</span>
                        ))}
                    </span>, true, editavel)}
                {/* Sem perna aberta o campo aparece VAZIO, com o placeholder genérico: nada ali vale,
                    então mostrar o que foi digitado — ou o herdado, no lugar do exemplo — só faria
                    parecer que conta. O texto continua no estado e volta assim que uma perna
                    reabrir; quem apaga de verdade é o Salvar. */}
                <FinInput type="text" disabled value="" placeholder={exemplo} />
            </FinField>
        );
    }
    return ponta(abertas.map((perna, i) => {
        const campo = local[perna];
        const doHerdado = badge(perna);
        return (
            <FinField key={chave + '-' + perna} style={ESTILO_CAMPO_LOCAL}>
                {/* A label só no primeiro: "Origens" já fala pelos dois campos. */}
                {rotuloHerdado(i === 0 ? rotuloDaPonta : '', doHerdado, (
                    <span className="d-inline-flex align-items-center gap-1">{badgesDoCampo(perna)}</span>
                ), false, editavel)}
                {/* minLength=2, na origem e no destino: aceita sigla de UF ("SP"). */}
                <FinInput type="text" disabled={!editavel} minLength={2}
                    placeholder={doHerdado?.ativo === false ? LOCAL_SEM_HERDADO : (doHerdado?.valor || exemplo)}
                    value={t[campo.campo] ?? ''}
                    onChange={e => onDigitar(campo, e.target.value)} />
            </FinField>
        );
    }));
}

/** Data em branco vale a da solicitação: o badge diz qual é. */
const vazioVale = (de, valor) => (valor ? { valor, ativo: true, dica: `Vazio vale ${de}: ${valor}` } : null);
const dataLimpa = (v) => String(v ?? '').replace(/[_\s]/g, '').trim();

/**
 * Uma coluna por direção: o switch que abre o detalhe, a data e as janelas de horário.
 *
 * Os switches são só da TELA: o da direção abre a data e os horários daquele voo, e cada horário
 * (saída, chegada) abre o De e o Até. Fechado, o campo não vai ao banco e vale o herdado — o valor
 * fica à vista até o Salvar, para voltar se o switch reabrir.
 */
function DatasHorarios({ trecho: t, heranca, editavel, campo, ligarDirecao, marcarSemTrecho }) {
    const direcoes = [
        {
            chave: 'ida', abrir: 'detalharIda', rotulo: 'Ida', horarios: HORARIOS.ida,
            data: { campo: 'dataIda', label: 'Data da ida', herdada: heranca.dataIda },
        },
        {
            chave: 'retorno', abrir: 'detalharRetorno', rotulo: 'Volta', horarios: HORARIOS.retorno,
            data: { campo: 'dataRetorno', label: 'Data da volta', herdada: heranca.dataRetorno },
        },
    ];

    return (
        <div className="fin-pp__trecho-sentidos" style={{ marginTop: 0 }}>
            {direcoes.map(dir => {
                const ligada = !!t[dir.abrir];
                // Com os DOIS horários ligados os blocos crescem e ocupam a linha. Com um desligado
                // não: o switch solto do outro ficaria jogado na ponta direita.
                const horariosLigados = dir.horarios.every(h => t[h.abrir]);
                const larguraHorarios = larguraDosHorarios(dir.horarios.map(h => !!t[h.abrir]));
                return (
                    <div key={dir.chave} className="fin-pp__trecho-sentido">
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: '4px 10px' }}>
                            {/* Este switch NÃO é a direção do trecho — a direção é o "Sem trecho"
                                ao lado. Ele abre e fecha o detalhe daquele voo, e fechado grava
                                vazio: é assim que se diz "esta direção não tem valor próprio, vale
                                o herdado". Os dois valem em toda tela que edita um trecho: a malha
                                é espelho editável, e o que ela muda vale só nela. */}
                            <FinSwitch label={dir.rotulo} checked={ligada} disabled={!editavel}
                                onChange={e => ligarDirecao(dir, e.target.checked)} />
                            <span className="fin-pp__cell-muted" aria-hidden="true">—</span>
                            <FinCheck label={`Sem trecho de ${dir.rotulo}`} checked={t[dir.chave] === false}
                                disabled={!editavel}
                                onChange={e => marcarSemTrecho(dir, e.target.checked)} />
                        </div>
                        {/* Data e horários na mesma linha, alinhados pela base (o padrão do
                            .fin-pp__filters). Os dois horários são um item só da linha, então quebram
                            juntos. Ligado, o switch fica em cima dos campos dele, na altura do rótulo
                            da data; desligado, só o switch, alinhado ao centro do input da data. */}
                        {ligada && (
                            <div className="fin-pp__filters">
                                <FinField style={{ flex: '0 1 170px', minWidth: `min(${LARGURA_DATA_PISO}px, 100%)` }}>
                                    <div style={CABECALHO_BLOCO}>
                                        {rotuloHerdado(dir.data.label,
                                            dataLimpa(t[dir.data.campo]) ? null : vazioVale('a da solicitação', dataLimpa(dir.data.herdada)),
                                            null, false, editavel)}
                                    </div>
                                    {/* Sem rótulo próprio: o do campo leva o badge. */}
                                    <Data labelExterno label="" value={t[dir.data.campo]}
                                        onChange={val => campo(dir.data.campo, val)}
                                        disabled={!editavel} />
                                </FinField>
                                <div className="d-flex flex-wrap align-items-end"
                                    style={{ gap: '14px 16px', minWidth: 0, flex: `${horariosLigados ? 1 : 0} 1 ${larguraHorarios}px` }}>
                                    {dir.horarios.map(h => (t[h.abrir] ? (
                                        <div key={`${h.abrir}-ligado`} className="fin-pp__field"
                                            style={{ flex: `${horariosLigados ? 1 : 0} 0 ${LARGURA_HORARIO_LIGADO}px`,
                                                minWidth: `min(${LARGURA_HORARIO_LIGADO}px, 100%)` }}>
                                            <div className="fin-pp__field-head" style={CABECALHO_BLOCO}>
                                                <FinSwitch label={h.rotulo} checked
                                                    disabled={!editavel}
                                                    onChange={e => campo(h.abrir, e.target.checked)} />
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="fin-pp__cell-muted">de</span>
                                                <input type="time" className="fin-pp__input" style={HORA_NO_BLOCO}
                                                    aria-label={`${h.rotulo}: de`}
                                                    disabled={!editavel} value={t[h.de] ?? ''}
                                                    onChange={e => campo(h.de, e.target.value)} />
                                                <span className="fin-pp__cell-muted">até</span>
                                                <input type="time" className="fin-pp__input" style={HORA_NO_BLOCO}
                                                    aria-label={`${h.rotulo}: até`}
                                                    disabled={!editavel} value={t[h.ate] ?? ''}
                                                    onChange={e => campo(h.ate, e.target.value)} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div key={`${h.abrir}-desligado`} className="fin-pp__field-head" style={SWITCH_DESLIGADO}>
                                            <FinSwitch label={h.rotulo} checked={false}
                                                disabled={!editavel}
                                                onChange={e => campo(h.abrir, e.target.checked)} />
                                        </div>
                                    )))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ---------------------------------------------------------------------------------------------
// O formulário
// ---------------------------------------------------------------------------------------------

/**
 * @param {Object}   trecho             valores + as marcas de tela (`origemIdaAberta`, `detalharIda`…)
 * @param {Object}   referencias        { origemPai, destino } — de onde as pontas herdam
 * @param {Object}   heranca            { dataIda, dataRetorno } — as datas do pedido, em badge
 * @param {boolean}  editavel
 * @param {Object}   [origemSolicitacao] { opcoes: [{ chave, nome }], aoTrocar?, fixa? } — sem ela, o
 *                                       select não aparece. `aoTrocar` é efeito da TELA (levar o card
 *                                       para o novo grupo); o valor em si quem grava é o componente.
 *                                       `fixa`: o select aparece travado no valor (a origem editando a
 *                                       si mesma)
 * @param {number}   [tetoPassageiros]  quantidade da origem: placeholder e máximo. Sem ela, livre
 * @param {Object}   [observacao]       { label, placeholder, somenteLeitura, ocultarVazia, title,
 *                                        id, original } — sem ela, o bloco não aparece. O texto sai
 *                                        de `trecho.observacao`, como todo o resto
 * @param {Function} onTrecho           (novoTrecho) => void — O CANAL ÚNICO. Todo gesto vira um
 *                                      trecho novo AQUI DENTRO, com a regra já aplicada, e a tela
 *                                      só guarda o que chega. É o que acabou com cada tela
 *                                      reimplementando "ligar a direção" e "religar o herdado" do
 *                                      seu jeito — e divergindo.
 */
export default function FormTrecho({
    trecho: t, referencias = {}, heranca = {}, editavel = true,
    origemSolicitacao = null, tetoPassageiros = null, observacao = null,
    onTrecho,
}) {
    // AS REGRAS MORAM AQUI, não nas telas: cada gesto vira um trecho novo, já com a regra
    // aplicada, e sai pelo canal único. Enquanto cada tela montava o seu, elas divergiram.
    const campo = (k, v) => onTrecho({ ...t, [k]: v });
    const digitarLocal = (local, valor) => onTrecho(comLocalDigitado(t, local, valor));
    const ligarDirecao = (dir, ligar) => onTrecho(comDirecaoLigada(t, dir, ligar));
    const marcarSemTrecho = (dir, sem) => onTrecho(comSemTrecho(t, dir, sem));

    const teto = tetoPassageiros ?? null;
    const dicaTeto = teto != null
        ? `Vazio vale a quantidade da origem (${teto}), que é também o máximo.`
        : undefined;

    const obs = observacao;
    const mostrarObs = obs && !(obs.ocultarVazia && String(t.observacao ?? '').trim() === '');

    // A MOLDURA (fundo, padding, borda) fica com quem chama: o cartão da solicitação tem, o modal
    // não. As linhas são as mesmas nas duas telas e em qualquer largura: em cima a origem da
    // solicitação e a quantidade, embaixo origem e destino.
    return (
        <div className="fin-pp__trecho-form">
            <div className="fin-pp__filters">
                {origemSolicitacao && (
                    /* Largura AUTOMÁTICA, e pelo VALOR SELECIONADO: o `field-sizing: content` do
                       `.fin-pp__trecho-solic` (no CSS) faz o select medir a opção escolhida — "Sem
                       origem (trecho solto)" dá 222px, "São Paulo" dá 125 — em vez de medir sempre a
                       opção mais longa da lista.
                       O piso de 160 é o do RÓTULO: "ORIGEM DA SOLICITAÇÃO" mede 152px, e abaixo
                       disso é ele que começa a cortar, não o valor.
                       A linha é só dele e da quantidade: ele cresce até 320, e no aperto é ele
                       que encolhe primeiro (`flex-shrink: 100`), até o piso de 160. */
                    <FinField label="Origem da solicitação" className="fin-pp__trecho-solic"
                        style={{ flex: '1 100 auto', minWidth: 'min(160px, 100%)', maxWidth: 320 }}>
                        <select className="fin-pp__select" disabled={!editavel || !!origemSolicitacao.fixa}
                            value={t.origem_id ?? ''}
                            onChange={e => {
                                const valor = e.target.value || null;
                                campo('origem_id', valor);
                                if (origemSolicitacao.aoTrocar) origemSolicitacao.aoTrocar(valor);
                            }}>
                            <option value="">Sem origem (trecho solto)</option>
                            {origemSolicitacao.opcoes.map(o => (
                                <option key={o.chave} value={o.chave}>{o.nome || '(sem nome)'}</option>
                            ))}
                        </select>
                    </FinField>
                )}
                {/* O rótulo "QTD. PASSAGEIROS" mede 104px: o piso de 180 do `.fin-pp__field`
                    sobrava, e era ele que empurrava a origem. Fica à direita, na linha da origem da
                    solicitação. */}
                <FinField label="Qtd. passageiros" className="fin-pp__trecho-qtd"
                    style={{ flex: '0 1 120px', minWidth: 'min(120px, 100%)', marginLeft: 'auto' }}>
                    {/* Sem separador de milhar: é contagem de pessoas, não valor. Vazio vale a
                        quantidade da origem, que aparece no placeholder, e ela é o TETO: o trecho
                        não leva mais gente do que a origem pediu. O teto é o mesmo para todos os
                        trechos dela — não é um saldo repartido, porque os trechos são caminhos da
                        mesma gente. */}
                    <FinNumber value={t.quantidadePassageiros ?? ''} decimalScale={0}
                        thousandSeparator={false} allowNegative={false} disabled={!editavel}
                        placeholder={teto != null ? String(teto) : undefined}
                        title={dicaTeto}
                        onValueChange={v => {
                            const n = v.floatValue ?? null;
                            // Digitar acima do teto não é bloqueado: o campo corrige para o máximo e
                            // diz por quê. Bloquear a tecla deixaria o usuário sem saber por que o
                            // número não entra.
                            if (teto != null && n != null && n > teto) {
                                toast.warning(`A origem pediu ${teto} passageiro${teto !== 1 ? 's' : ''}: o trecho não leva mais que isso.`,
                                    { toastId: 'teto-passageiros' });
                            }
                            campo('quantidadePassageiros',
                                n === null || n <= 0 ? null : (teto == null ? n : Math.min(n, teto)));
                        }} />
                </FinField>
                {/* Origem e destino são um item só, SEMPRE na linha de baixo. Na mesma linha da
                    origem da solicitação e da quantidade, abrir a segunda perna de um local fazia a
                    linha quebrar no meio da edição, e a quantidade e os locais trocavam de lugar. */}
                <div className="d-flex flex-wrap align-items-end fin-pp__trecho-locais"
                    style={{ gap: '14px 16px', flex: '1 1 100%', minWidth: 0 }}>
                    {['origem', 'destino'].map(chave => (
                        <Ponta key={chave} chave={chave} trecho={t} editavel={editavel}
                            referencias={referencias}
                            onDigitar={digitarLocal}
                            onAlternarHerdado={(local, ligado) => campo(local.herdado, ligado)}
                            onAlternarCampo={(local, aberto) => campo(local.aberto, aberto)} />
                    ))}
                </div>
            </div>

            {/* Uma coluna por direção. Os switches são só da tela: o da direção abre a data e os
                horários daquele voo, e cada horário (saída, chegada) abre o De e o Até. Fechado vale
                o herdado. O "Sem trecho" ao lado é gravado: marcado, a direção não é pedida. */}
            <div className="fin-pp__label" style={{ marginTop: 16, marginBottom: 8 }}>Datas e horários</div>
            <DatasHorarios trecho={t} heranca={heranca} editavel={editavel}
                campo={campo} ligarDirecao={ligarDirecao} marcarSemTrecho={marcarSemTrecho} />

            {/* Recado do RSVP ao Aéreo sobre este trecho, e é o último bloco nas duas telas. A
                observação da solicitação também aparece para o Aéreo: esta não a substitui. */}
            {mostrarObs && (
                <div className="fin-pp__filters" style={{ marginTop: 12 }}>
                    <FinField full>
                        {/* O (i) guarda o texto do RSVP. Ele é preciso desde que a observação virou
                            sobrescrevível: o que está no campo é o do Aéreo, e sem isto o recado do
                            pedido sumiria da tela ao ser sobrescrito. Sem original, nada aparece. */}
                        {/* Cabeçalho próprio, e não o `rotuloHerdado`: lá a ação vai para a direita
                            da linha (é onde ficam os badges de perna), e aqui o (i) é leitura do
                            rótulo — fica colado nele, à esquerda. */}
                        <div className="fin-pp__field-head" style={{ justifyContent: 'flex-start', gap: 0, minWidth: 0 }}>
                            <label className="fin-pp__label" style={ROTULO_UMA_LINHA}>{obs.label ?? 'Observação'}</label>
                            {/* Só quando há sobrescrita: sem ela o campo já vale o texto do RSVP, e
                                um (i) que abre o mesmo que está valendo é ruído. */}
                            <PillObservacao texto={obs.original} semBadge rotulo="Observação da solicitação" />
                        </div>
                        <FinTextarea rows={3} maxLength={2000}
                            id={obs.id} title={obs.title}
                            disabled={!editavel && !obs.somenteLeitura}
                            readOnly={!!obs.somenteLeitura}
                            placeholder={obs.placeholder}
                            style={obs.somenteLeitura ? { background: 'var(--fin-sf2)', resize: 'vertical' } : undefined}
                            value={t.observacao ?? ''}
                            onChange={obs.somenteLeitura ? undefined : (e => campo('observacao', e.target.value))} />
                    </FinField>
                </div>
            )}
        </div>
    );
}
